import React from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "@/shared/components/Logo";

/* The chrome-less first-run frame, shared by the setup wizard and the
   name-your-workspace screen that precedes it when the caller has no org.
   Lifted out of Onboarding.tsx unchanged — a screen that is one question and a
   Continue button must look like a step of the wizard, because it is one. */

export const inputCls =
  "w-full h-11 px-3.5 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[15px] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]";

/* Shared frame: eyebrow, question, whatever the step needs, then the footer.
   Every step gets the same rhythm so advancing feels like one motion. */
interface StepProps {
  /** 0-based; the counter renders `index + 1`. Omit `total` to hide it — the
      org screen is one question, not step 1 of anything. */
  index: number;
  total?: number;
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Absent on the first step — there is nothing behind it. */
  onBack?: () => void;
  footer: React.ReactNode;
}

export function Step({ index, total, eyebrow, title, subtitle, children, onBack, footer }: StepProps) {
  return (
    <div key={index} className="fade-in-up w-full max-w-[560px] flex flex-col gap-6">
      <div className="flex items-center gap-2">
        {onBack && (
          <button
            onClick={onBack}
            aria-label="Back"
            className="press w-6 h-6 -ml-1 rounded-full flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        )}
        <span className="section-label">{eyebrow}</span>
        {total && (
          <span className="ml-auto font-mono text-[10px] text-[var(--text-quaternary)]">
            {index + 1} / {total}
          </span>
        )}
      </div>

      <div>
        <h1 className="text-[26px] leading-[1.2] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 text-[13px] leading-[1.55] text-[var(--text-tertiary)]">{subtitle}</p>
        )}
      </div>

      {children}

      {footer}
    </div>
  );
}

/* Primary advance. Kept local rather than BrandButton because these are
   h-11 to match the h-11 fields — a h-8 button under a h-11 input reads as
   an afterthought. */
export function NextButton({ disabled, children = "Continue", ...rest }: React.ComponentProps<"button">) {
  return (
    <button
      {...rest}
      disabled={disabled}
      className={`press inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-[10px] text-[13.5px] font-medium bg-[var(--brand)] text-[var(--brand-foreground)] transition-opacity ${
        disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--brand-hover)]"
      }`}
    >
      {children}
      <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
    </button>
  );
}

/** What went wrong with the last server call. Rendered under the footer so a
    failure never moves the question. */
export function StepError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="fade-in text-[12px] leading-[1.5] text-[var(--danger)]">
      {message}
    </p>
  );
}

/** The page the frame sits on. Both first-run screens are full-bleed and
    chrome-less: there is no workspace to put chrome around yet. */
export function OnboardingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-screen w-screen bg-[var(--canvas)] text-[var(--text-primary)] flex flex-col overflow-y-auto">
      <div className="shrink-0 px-6 py-5">
        <Logo />
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">{children}</div>
    </div>
  );
}
